import { useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Button } from "./Button";

interface ModalProps {
  open: boolean;
  title: string;
  description?: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  size?: "default" | "wide";
}

export function Modal({
  open,
  title,
  description,
  onClose,
  children,
  footer,
  size = "default",
}: ModalProps) {
  useEffect(() => {
    if (!open) {
      return;
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        onClose();
      }
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const widthClass = size === "wide" ? "edlab-dialog-wide" : "edlab-dialog-default";

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3.5">
      <button
        type="button"
        className="absolute inset-0 bg-[#182a3640] backdrop-blur-[5px]"
        aria-label="Close dialog"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="modal-title"
        className={["edlab-dialog relative z-10 flex max-h-[90vh] flex-col", widthClass].join(" ")}
      >
        <div className="flex shrink-0 items-start justify-between gap-[15px] border-b border-edlab-line bg-white px-[26px] pt-6 pb-[18px]">
          <div>
            <h2
              id="modal-title"
              className="text-[22px] font-semibold tracking-[-0.6px] text-edlab-ink"
            >
              {title}
            </h2>
            {description ? (
              <p className="mt-[5px] text-[13px] text-edlab-muted">{description}</p>
            ) : null}
          </div>
          <Button
            variant="ghost"
            type="button"
            onClick={onClose}
            aria-label="Close dialog"
            className="min-h-[34px] min-w-[34px] rounded-lg border border-edlab-line bg-white px-0 text-edlab-muted hover:bg-edlab-bg"
          >
            ✕
          </Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-[26px] py-[23px]">{children}</div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-[9px] border-t border-edlab-line bg-[#fcfdfd] px-[26px] py-[17px]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}
